import type { MessageDefinition, MessageInstance, ValidationResult } from '@k-ucap/shared';
import { createMessageDictionaryService } from '@/domain/message/MessageDictionaryService.js';
import { createMessageValidationService } from '@/domain/message/MessageValidationService.js';
import { getGlobalVirtualMessageAdapter } from '@/infrastructure/adapters/VirtualMessageAdapter.js';
import { createRepositories } from '@/infrastructure/repositories/RepositoryFactory.js';
import { GisMessageProjectionService } from '@/domain/gis/GisMessageProjectionService.js';
import { getAppConfig } from '@/config/appConfig.js';
import { getFirebaseStatus } from '@/infrastructure/firebase/firebaseStatus.js';
import { firebaseFunctions } from '@/infrastructure/firebase/firebaseFunctions.js';
import { httpsCallable, getFunctions } from 'firebase/functions';
import { createRecentAssetsService } from '@/domain/recent-assets/RecentAssetsService.js';
import { useFavoriteStore } from '@/stores/useFavoriteStore.js';
import { useAuthStore } from '@/stores/useAuthStore.js';
import { createTemplateResolverService } from '@/domain/asset/TemplateResolverService.js';
import { createExpressionResolverService } from '@/domain/asset/ExpressionResolverService.js';
import { createParameterSetService } from '@/domain/asset/ParameterSetService.js';
import { createDatasetService } from '@/domain/asset/DatasetService.js';
import { createCsvParserService } from '@/domain/asset/CsvParserService.js';
import { createScenarioAssetResolverService } from '@/domain/asset/ScenarioAssetResolverService.js';
import { createInMemoryMessageTemplateRepository } from '@/infrastructure/repositories/InMemoryMessageTemplateRepository.js';
import { createInMemoryParameterSetRepository } from '@/infrastructure/repositories/InMemoryParameterSetRepository.js';
import { createInMemoryDatasetRepository } from '@/infrastructure/repositories/InMemoryDatasetRepository.js';
import { TestAssetType, AssetStatus, ParameterScope, SessionStatus } from '@k-ucap/shared';

export type DiagnosticStatus = 'idle' | 'loading' | 'pass' | 'warn' | 'fail';

export interface DiagnosticResult {
  component: string;
  status: DiagnosticStatus;
  durationMs: number;
  error?: string;
  recommendation?: string;
}

export interface SelfTestSummary {
  startedAt: string;
  completedAt: string;
  totalDurationMs: number;
  overall: DiagnosticStatus;
  results: DiagnosticResult[];
}

export class SelfTestService {
  static async run(projectId: string | undefined): Promise<SelfTestSummary> {
    const startedAt = new Date().toISOString();
    const results: DiagnosticResult[] = [];

    const runCheck = async (name: string, fn: () => Promise<void>): Promise<DiagnosticResult> => {
      const start = performance.now();
      try {
        await fn();
        return { component: name, status: 'pass', durationMs: Math.round(performance.now() - start) };
      } catch (err) {
        const error = err instanceof Error ? err.message : String(err);
        return {
          component: name,
          status: 'fail',
          durationMs: Math.round(performance.now() - start),
          error,
          recommendation: `${name} 설정을 확인하세요.`,
        };
      }
    };

    results.push(
      await runCheck('Message Dictionary', async () => {
        const service = createMessageDictionaryService();
        const messages = service.getAllMessages();
        if (messages.length === 0) throw new Error('No messages loaded');
      })
    );

    let testMessage: MessageInstance | undefined;
    let testDefinition: MessageDefinition | undefined;
    results.push(
      await runCheck('Message Generator', async () => {
        const dictionaryService = createMessageDictionaryService();
        const definition = dictionaryService.getMessageByType(101);
        if (!definition) throw new Error('Message #101 not found');
        testDefinition = definition;
        const demoPayload: Record<string, unknown> = {};
        for (const field of definition.fields) {
          demoPayload[field.key] = field.demoValue !== undefined ? field.demoValue : field.defaultValue;
        }
        testMessage = {
          id: `selftest-${Date.now()}`,
          messageType: definition.messageType,
          standard: definition.standard,
          name: definition.name,
          source: definition.sourceRole,
          target: definition.targetRole,
          direction: 'TX',
          timestamp: new Date().toISOString(),
          fields: demoPayload,
          projectId: projectId ?? 'self-test',
          environment: 'development',
          sequenceNumber: 1,
        };
      })
    );

    results.push(
      await runCheck('Event Bus', async () => {
        if (!testMessage) throw new Error('No test message');
        const adapter = getGlobalVirtualMessageAdapter();
        await adapter.publish(testMessage);
      })
    );

    results.push(
      await runCheck('Analyzer', async () => {
        if (!testMessage || !testDefinition) throw new Error('No test message/definition');
        const validationService = createMessageValidationService();
        const result: ValidationResult = validationService.validate(testDefinition, testMessage.fields);
        if (result.severity === 'FAIL') throw new Error('Self-test message validation failed');
      })
    );

    results.push(
      await runCheck('GIS Projection', async () => {
        if (!testMessage) throw new Error('No test message');
        const projection = GisMessageProjectionService.project(testMessage, 'UAV-TEST');
        if (!projection) throw new Error('GIS projection returned null');
      })
    );

    results.push(
      await runCheck('Repository', async () => {
        const { projectRepository } = createRepositories();
        await projectRepository.findAll();
      })
    );

    results.push(
      await runCheck('Firebase Functions', async () => {
        const config = getAppConfig();
        const status = getFirebaseStatus();
        if (!firebaseFunctions || status.mode !== 'FIREBASE') {
          return; // skip in mock mode
        }
        const functions = getFunctions(undefined, config.functionsRegion);
        const health = httpsCallable(functions, 'healthCheck' as never);
        await health({});
      })
    );

    results.push(
      await runCheck('Recent Assets', async () => {
        if (!projectId) throw new Error('Project not selected');
        const service = createRecentAssetsService(createRepositories());
        const assets = await service.loadRecentAssets(projectId);
        if (!Array.isArray(assets)) throw new Error('RecentAssetsService returned invalid data');
      })
    );

    results.push(
      await runCheck('Favorites', async () => {
        const favorites = useFavoriteStore.getState().getFavorites();
        if (!Array.isArray(favorites)) throw new Error('FavoriteStore returned invalid data');
      })
    );

    results.push(
      await runCheck('Test Asset Template Resolve', async () => {
        const templateResolver = createTemplateResolverService(createExpressionResolverService());
        const template: import('@k-ucap/shared').MessageTemplate = {
          id: 'template-selftest',
          projectId: 'self-test',
          assetType: TestAssetType.MESSAGE_TEMPLATE,
          name: 'Self-Test Template',
          description: '',
          messageType: 101,
          payload: { altitudeM: 1000, speedMps: 50 },
          parameterSetIds: [],
          datasetBindings: [],
          tags: [],
          version: '1.0',
          revision: 1,
          status: AssetStatus.ACTIVE,
          createdBy: 'self-test',
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          metadata: {},
        };
        const resolved = templateResolver.resolve(template, undefined, {
          context: { sequence: 1, timestamp: new Date().toISOString() },
        });
        if (resolved.resolvedPayload.altitudeM !== 1000) throw new Error('Template payload resolve failed');
      })
    );

    results.push(
      await runCheck('Test Asset Parameter Set', async () => {
        const parameterSetService = createParameterSetService();
        const ps: import('@k-ucap/shared').ParameterSet = {
          id: 'ps-selftest',
          projectId: 'self-test',
          assetType: TestAssetType.PARAMETER_SET,
          name: 'Self-Test Parameter Set',
          description: '',
          scope: ParameterScope.GLOBAL,
          values: { altitudeM: 2000 },
          tags: [],
          version: '1.0',
          revision: 1,
          status: AssetStatus.ACTIVE,
          createdBy: 'self-test',
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          metadata: {},
        };
        const merged = parameterSetService.merge([ps]);
        if (merged.altitudeM !== 2000) throw new Error('Parameter set merge failed');
      })
    );

    results.push(
      await runCheck('Test Asset Dataset Binding', async () => {
        const csvParser = createCsvParserService();
        const datasetService = createDatasetService(csvParser);
        const csv = 'lat,lon,alt\n37.5,127.0,100\n37.6,127.1,200';
        const dataset = datasetService.importFromCsv(csv, {
          name: 'Self-Test Dataset',
          projectId: 'self-test',
          createdBy: 'self-test',
        });
        const binding = { datasetId: dataset.id, column: 'lat', fieldKey: 'latitude', required: true };
        dataset.bindings = [binding];
        const rows = datasetService.getRows(dataset);
        if (rows.length !== 2) throw new Error('Dataset row count mismatch');
        const firstRow = rows[0];
        if (firstRow.lat !== 37.5) throw new Error('Dataset binding value mismatch');
      })
    );

    results.push(
      await runCheck('Test Asset Expression', async () => {
        const resolver = createExpressionResolverService();
        const result = resolver.resolve('${sequence}', { sequence: 42, timestamp: new Date().toISOString() });
        if (result !== 42) throw new Error('Expression resolve failed');
      })
    );

    results.push(
      await runCheck('Scenario Asset Reference Resolve', async () => {
        const messageTemplateRepository = createInMemoryMessageTemplateRepository();
        const parameterSetRepository = createInMemoryParameterSetRepository();
        const datasetRepository = createInMemoryDatasetRepository();
        const template: import('@k-ucap/shared').MessageTemplate = {
          id: 'template-scenario',
          projectId: 'self-test',
          assetType: TestAssetType.MESSAGE_TEMPLATE,
          name: 'Scenario Template',
          description: '',
          messageType: 101,
          payload: { altitudeM: 500 },
          parameterSetIds: [],
          datasetBindings: [],
          tags: [],
          version: '1.0',
          revision: 1,
          status: AssetStatus.ACTIVE,
          createdBy: 'self-test',
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          metadata: {},
        };
        await messageTemplateRepository.save(template);
        const rule: import('@k-ucap/shared').MessageRule = {
          id: 'rule-selftest',
          messageType: 101,
          enabled: true,
          payloadTemplate: {},
          fieldRules: {},
          generationMode: 'SINGLE',
          intervalMs: 1000,
          count: 1,
          startDelayMs: 0,
          randomization: false,
          faultInjection: false,
          order: 0,
          messageTemplateId: template.id,
        };
        const resolver = createScenarioAssetResolverService({
          messageTemplateRepository,
          parameterSetRepository,
          datasetRepository,
          templateResolver: createTemplateResolverService(createExpressionResolverService()),
          parameterSetService: createParameterSetService(),
        });
        const resolved = await resolver.resolveMessageRules([rule]);
        if (resolved[0].payloadTemplate.altitudeM !== 500) throw new Error('Scenario asset reference resolve failed');
      })
    );

    results.push(
      await runCheck('Session Snapshot', async () => {
        const snapshot = {
          templateId: 'template-snapshot',
          templateVersion: '1.0',
          parameterSetId: undefined,
          datasetId: undefined,
          datasetRow: undefined,
        };
        if (!snapshot.templateId) throw new Error('Session snapshot missing templateId');
      })
    );

    results.push(
      await runCheck('Session Context Store', async () => {
        const { useWorkbenchContextStore } = await import('@/stores/useWorkbenchContextStore.js');
        const store = useWorkbenchContextStore.getState();
        store.selectSession('session-selftest');
        if (useWorkbenchContextStore.getState().selectedSessionId !== 'session-selftest') {
          throw new Error('Session context select failed');
        }
        useWorkbenchContextStore.getState().resetAll();
        if (useWorkbenchContextStore.getState().selectedSessionId !== null) {
          throw new Error('Session context reset failed');
        }
      })
    );

    results.push(
      await runCheck('Deep Link Builder', async () => {
        const { buildDeepLink } = await import('@/hooks/useDeepLinkContext.js');
        const link = buildDeepLink('/analyzer', { sessionId: 's1', messageId: 'm1' });
        if (!link.includes('sessionId=s1') || !link.includes('messageId=m1')) {
          throw new Error('Deep link build failed');
        }
      })
    );

    results.push(
      await runCheck('Evidence Creation', async () => {
        const { EvidenceService } = await import('@/domain/evidence/EvidenceService.js');
        const session = {
          id: 'session-evidence-test',
          projectId: 'p1',
          environment: 'development',
          startedAt: new Date().toISOString(),
          status: SessionStatus.RUNNING,
          messageCount: 0,
          passCount: 0,
          warnCount: 0,
          failCount: 0,
          createdBy: 'self-test',
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };
        const event = {
          id: 'event-evidence-test',
          projectId: 'p1',
          environment: 'development',
          runId: 'run-1',
          messageType: 101,
          standard: 'STANAG4586',
          name: 'Test',
          source: 'A',
          target: 'B',
          sourceRole: 'A',
          targetRole: 'B',
          direction: 'TX' as const,
          timestamp: new Date().toISOString(),
          payload: {},
          fields: {},
          validation: 'FAIL',
          validationStatus: 'FAIL',
          sequenceNumber: 1,
          createdBy: 'self-test',
          createdAt: new Date().toISOString(),
        };
        const result = {
          severity: 'FAIL' as const,
          issues: [{ severity: 'FAIL' as const, code: 'MINIMUM', message: 'min violation', path: 'altitudeM' }],
          timestamp: new Date().toISOString(),
        };
        const items = EvidenceService.createFromValidation(session, event, result);
        if (items.length === 0) throw new Error('Evidence creation returned empty');
        if (items[0].sessionId !== session.id) throw new Error('Evidence sessionId mismatch');
      })
    );

    results.push(
      await runCheck('Web Worker', async () => {
        if (typeof Worker === 'undefined') throw new Error('Web Worker not supported');
      })
    );

    results.push(
      await runCheck('Auth Context', async () => {
        const userProfile = useAuthStore.getState().userProfile;
        if (!userProfile) throw new Error('User profile not loaded');
      })
    );

    results.push(
      await runCheck('Project Context', async () => {
        if (!projectId) throw new Error('Project not selected');
      })
    );

    const completedAt = new Date().toISOString();
    const totalDurationMs = results.reduce((sum, r) => sum + r.durationMs, 0);
    const overall: DiagnosticStatus = results.some((r) => r.status === 'fail')
      ? 'fail'
      : results.some((r) => r.status === 'warn')
        ? 'warn'
        : 'pass';

    return { startedAt, completedAt, totalDurationMs, overall, results };
  }
}

export default SelfTestService;
